/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback, useBatchingCallback } from '@web-stories-wp/react';
import {
  DANGER_ZONE_HEIGHT,
  FULLBLEED_HEIGHT,
  PAGE_WIDTH,
} from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import objectWithout from '../../utils/objectWithout';
import { useStory, ELEMENT_TYPES } from '../../app/story';
import { useLayout } from '../../app/layout';
import { createNewElement } from '../../elements';
import { getHTMLFormatters } from '../richText/htmlManipulation';
import usePageAsCanvas from '../../utils/usePageAsCanvas';
import { remapTextBoxAttachment } from '../../utils/textBoxAttachment';
import { ZOOM_SETTING } from '../../constants';
import { getElementProperties } from './useInsertElement';
import useFocusCanvas from './useFocusCanvas';

const SCRIM_PADDING = 24;

function useInsertTextSet(shouldUseSmartColor = false) {
  const { calculateAccessibleTextColors } = usePageAsCanvas();
  const focusCanvas = useFocusCanvas();
  const { setZoomSetting } = useLayout(({ actions }) => ({
    setZoomSetting: actions.setZoomSetting,
  }));

  const { addElements, setSelectedElementsById } = useStory(
    ({ actions: { addElements, setSelectedElementsById } }) => {
      return {
        addElements,
        setSelectedElementsById,
      };
    }
  );

  const insertTextSet = useBatchingCallback(
    async (toAdd, applySmartColor = shouldUseSmartColor) => {
      const htmlFormatters = getHTMLFormatters();
      const { setColor } = htmlFormatters;
      const addedElements = [];

      const hasPredefinedColor = toAdd.some((element) =>
        element.content?.includes('color:')
      );

      let textElementsContrasts = [];
      let firstValidColor = null;

      const white = {
        r: 255,
        g: 255,
        b: 255,
      };
      const black = {
        r: 0,
        g: 0,
        b: 0,
      };
      const whiteScrim = {
        ...white,
        a: 0.3,
      };
      const blackScrim = {
        ...black,
        a: 0.3,
      };
      let preferredScrimColor, scrimsCount, useScrim;

      // Insert scrim as a first element if needed.
      if (applySmartColor && !hasPredefinedColor) {
        textElementsContrasts = await Promise.all(
          toAdd.map((element) =>
            element.type === 'text'
              ? calculateAccessibleTextColors(element)
              : null
          )
        );
        firstValidColor = textElementsContrasts.find((c) => c);
        const allColorsEqual = textElementsContrasts.every(
          (contrast) =>
            contrast === null ||
            (contrast.color.r === firstValidColor.color.r &&
              contrast.color.g === firstValidColor.color.g &&
              contrast.color.b === firstValidColor.color.b &&
              contrast.color.a === firstValidColor.color.a)
        );
        scrimsCount = textElementsContrasts.reduce(
          (acc, contrast) => (contrast?.backgroundColor ? acc + 1 : acc),
          0
        );
        const blackScrims = textElementsContrasts.reduce(
          (acc, contrast) =>
            contrast?.backgroundColor?.r === 0 &&
            contrast?.backgroundColor?.g === 0 &&
            contrast?.backgroundColor?.b === 0
              ? acc + 1
              : acc,
          0
        );
        preferredScrimColor =
          scrimsCount - blackScrims > 0 ? blackScrim : whiteScrim;

        useScrim = scrimsCount > 0 || allColorsEqual === false;

        if (useScrim) {
          const { textSetHeight, textSetWidth } = toAdd[0];
          const getMinDim = (dim) =>
            Math.min.apply(
              null,
              toAdd.map((e) => e[dim])
            );
          const x = getMinDim('x');
          const y = getMinDim('y');
          const scrim = {
            x: x - SCRIM_PADDING,
            y: y - SCRIM_PADDING,
            width: textSetWidth + SCRIM_PADDING * 2,
            height: textSetHeight + SCRIM_PADDING * 2,
            backgroundColor: {
              color: preferredScrimColor,
            },
            type: 'shape',
          };
          addedElements.push(
            createNewElement(
              ELEMENT_TYPES.SHAPE,
              getElementProperties(ELEMENT_TYPES.SHAPE, scrim)
            )
          );
        }
      }

      const elementIdTransferMap = {};
      const textSetElements = toAdd.map((element, index) => {
        const toInsert = objectWithout(element, [
          'id',
          'normalizedOffsetX',
          'normalizedOffsetY',
          'textSetWidth',
          'textSetHeight',
        ]);
        if (applySmartColor && !hasPredefinedColor) {
          // If scrim is used - adjust the colors, otherwise use defaults.
          const scrimContrastingTextColor =
            preferredScrimColor.r === 0 ? white : black;
          const autoColor = useScrim
            ? { color: scrimContrastingTextColor }
            : textElementsContrasts[index];

          if (element.type === 'text') {
            toInsert.content = setColor(toInsert.content, autoColor);
          }
          if (element.type === 'shape') {
            // So far we only use borders (no fill) or shapes with fill (no borders).
            if (element.border) {
              toInsert.border.color = firstValidColor;
            } else {
              toInsert.backgroundColor = firstValidColor;
            }
          }
        }
        const newElement = createNewElement(
          element.type,
          getElementProperties(element.type, toInsert)
        );
        if (element.id) {
          elementIdTransferMap[element.id] = newElement.id;
        }
        return newElement;
      });
      addedElements.push(
        ...textSetElements.map((element) =>
          remapTextBoxAttachment(element, elementIdTransferMap)
        )
      );

      setZoomSetting(ZOOM_SETTING.FIT);
      addElements({ elements: addedElements });
      // Select all added elements.
      setSelectedElementsById({
        elementIds: addedElements.map(({ id }) => id),
      });
      focusCanvas();
    },
    [
      calculateAccessibleTextColors,
      shouldUseSmartColor,
      addElements,
      focusCanvas,
      setSelectedElementsById,
      setZoomSetting,
    ]
  );

  const insertTextSetByOffset = useCallback(
    (elements, { offsetX, offsetY }) => {
      if (!elements.length) {
        return;
      }

      const positionedTextSet = elements
        .map((element) => {
          // Skip adding any elements that are outside of page.
          const x = element.normalizedOffsetX + offsetX;
          const y = element.normalizedOffsetY + offsetY;
          const { width, height } = element;
          if (
            x > PAGE_WIDTH ||
            x + width < 0 ||
            y > FULLBLEED_HEIGHT ||
            y + height < -DANGER_ZONE_HEIGHT
          ) {
            return null;
          }
          return {
            ...element,
            x,
            y,
          };
        })
        .filter((el) => el);

      insertTextSet(positionedTextSet, false /* Skips using smart color */);
    },
    [insertTextSet]
  );

  return { insertTextSet, insertTextSetByOffset };
}

export default useInsertTextSet;
